import React, { useState, useCallback, useEffect, useRef } from "react";
import _ from "lodash";
import {
  _getSelectedOption,
  _getBaseUrl,
  _isFirstRender,
  _getCSRFTokenValue,
  _getTileShape,
} from "../utils/helper";
import api from "../utils/api";
import StateView from "../utils/StateView";
import { MultiSelectDropdown, TextInput } from "../forms";

import { CKEditor } from '@ckeditor/ckeditor5-react';
import { ClassicEditor, Essentials, Paragraph, Bold, Italic, Heading, Link, List, BlockQuote, SourceEditing } from 'ckeditor5';
// import 'ckeditor5/ckeditor5.css';

import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";

const INITIAL_VALUES = {
  nid: null,
  title: "",
  body: "",
  type: "",
  status: 0,
  summary: "",
  image_shape: "",
  projectStatus: "",
  image_alt: "",
  transitieagenda: [],
  wijk: [],
  description: "",
  contactpersoon: "",
};

const FLEXI_BLOCKS = {
  text_content: {
    label: "Rich text",
    init_values: "",
  },
  photo_gallery: {
    label: "Afbeelding carousel",
    init_values: [],
  },
  video: {
    label: "Video",
    init_values: {
      videoId: '',
      videoType: '', // youtube || vimeo
      videoTitle: '',
    },
  },
}

const ProjectCreateEdit = (props) => {
  const [values, setValues] = useState(INITIAL_VALUES);
  const [formMode, setFormMode] = useState("create"); // create || edit
  const [userTransitieagenda, setUserTransitieagenda] = useState("");
  const [isAdmin, setIsAdmin] = useState("");
  const [isLoadingPosts, setIsLoadingPosts] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [response, setResponse] = useState(null);
  const [mediaId, setMediaId] = useState(null);
  const [fileName, setFileName] = useState("");
  const [imagePreview, setImagePreview] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [links, setLinks] = useState([]);
  const [linkInput, setLinkInput] = useState({ url: '', text: '' });
  const [partners, setPartners] = useState([]);
  const [partnerInput, setPartnerInput] = useState('');

  // Locatie
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [newAddress, setNewAddress] = useState({
    name: '',
    street: '',
    postcode: '',
    city: '',
    lat: '',
    lng: '',
    mapURL: '',
  });
  const [addresses, setAddresses] = useState([]);
  const [locatieError, setLocatieError] = useState({
    name: false,
    street: false,
    postcode: false,
    city: false,
    lat: false,
    lng: false,
  });

  //Related Nodes
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [allResults, setAllResults] = useState([]);
  const [selectedProjecten, setSelectedProjecten] = useState([]);
  const [selectedPersonen, setSelectedPersonen] = useState([]);
  const [selectedMaterialen, setSelectedMaterialen] = useState([]);
  const [activeModalType, setActiveModalType] = useState(null); // 'projecten' | 'personen' | 'materialen'
  const [addedItems, setAddedItems] = useState([]);

  const [categoryOptions, setCategoryOptions] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState([]);

  const [typeOptions, setTypeOptions] = useState([]);
  const [statusOptions, setStatusOptions] = useState([]);
  const [thumbnailOptions, setthumbnailOptions] = useState([]);
  const [transitieagendaOptions, setTransitieagendaOptions] = useState([]);
  const [wijkOptions, setWijkOptions] = useState([]);

  const [type, setType] = useState("");
  // const [transitieagendaColor, setTransitieagendaColor] = useState("");
  const [postStatus, setPostStatus] = useState("");
  const [action, setAction] = useState(null);

  const [flexiBlocks, setFlexiBlocks] = useState([]);

  const [newCategoryError, setNewCategoryError] = useState(null);

  const [newTagValue, setNewTagValue] = useState("");

  const [contactPersonenOptions, setContactPersonenOptions] = useState([]);

  const handleTransitieagendaDragEnd = (result) => {
    const { source, destination, type } = result;
    if (type === "TRANSITIEAGENDA") {
      const newTransitieagenda = Array.from(values.transitieagenda);
      const [moved] = newTransitieagenda.splice(source.index, 1);
      newTransitieagenda.splice(destination.index, 0, moved);
      setValues({ ...values, transitieagenda: newTransitieagenda });
      return;
    }
  };

  // onDragEnd — updates order
  const handleFlexiBlocksDragEnd = (result) => {
    const { source, destination, type } = result;

    // -----------------------------
    // DRAGGING PARENT LEVEL
    // -----------------------------
    if (type === "PARENT") {
      const newFlexiBlocks = Array.from(flexiBlocks);
      const [moved] = newFlexiBlocks.splice(source.index, 1);
      newFlexiBlocks.splice(destination.index, 0, moved);
      setFlexiBlocks(newFlexiBlocks);
      return;
    }

    // -----------------------------
    // DRAGGING CHILD LEVEL
    // -----------------------------
    const parentIndex = flexiBlocks.findIndex((p) => p.id === type);
    const parent = flexiBlocks[parentIndex];

    const newChildren = Array.from(parent.value);
    const [movedChild] = newChildren.splice(source.index, 1);
    newChildren.splice(destination.index, 0, movedChild);

    const newFlexiBlocks = Array.from(flexiBlocks);
    newFlexiBlocks[parentIndex] = {
      ...parent,
      value: newChildren,
    };
    setFlexiBlocks(newFlexiBlocks);
    return;
  };

  // Add new flexi field
  const addFlexiField = (type) => {
    let newFlexiFields = [...flexiBlocks];

    setFlexiBlocks([
      ...newFlexiFields,
      { id: Date.now().toString(), label: FLEXI_BLOCKS[type].label, type: type, expanded: true, value: FLEXI_BLOCKS[type].init_values },
    ]);
  };

  // Remove flexi field by id
  const removeFlexiField = (id) => {
    const updatedFields = flexiBlocks.filter((field) => field.id !== id);
    setFlexiBlocks(updatedFields);
  };

  // Remove image from image gallery flexi field
  const removeFromImageGallery = (fieldId, imageId) => {
    const updatedFields = flexiBlocks.map((field) => {
      if (field.id === fieldId) {
        const updatedImages = field.value.filter((image) => image.id !== imageId);
        return { ...field, value: updatedImages };
      }
      return field;
    });

    setFlexiBlocks(updatedFields);
  };

  // Toggle expand/collapse flexi field
  const toggleExpandField = (id) => {
    const updatedFields = flexiBlocks.map((field) => {
      if (field.id === id) {
        return { ...field, expanded: !field.expanded };
      }
      return field;
    });

    setFlexiBlocks(updatedFields);
  };

  // Upload image to create media entity for image gallery flexi block
  const mediaImageUpload = async (e, fieldId) => {
    const file = e.target.files[0];
    if (!file) return;

    const uploadData = new FormData();
    uploadData.append("image", file);

    try {
      const res = await fetch("/api/upload_media_image", {
        method: "POST",
        body: uploadData,
      });

      const result = await res.json();

      if (res.ok && result.status === "success") {
        // Update the specific flexi block with the new image
        const updatedFields = flexiBlocks.map((field) => {
          if (field.id === fieldId) {
            const updatedImages = [...field.value, {
              id: 'img_' + Date.now().toString(),
              mediaId: result.media_id,
              imgUrl: result.imgUrl,
              caption: "",
              altText: "",
              credit: ""
            }];
            return { ...field, value: updatedImages };
          }
          return field;
        });

        setFlexiBlocks(updatedFields);
      }
    } catch (err) {
      console.error("Image upload error:", err);
    }
  }

  useEffect(() => {
    // Get form mode: "create" or "edit"
    const container = document.querySelector("#project-create-edit-container");
    const mode = container.getAttribute("data-form-mode");
    const nid = container.getAttribute("data-nid");
    const userTID = container.getAttribute("data-user-transitieagenda-id");
    const userTColour = container.getAttribute("data-user-transitieagenda-colour");
    const userAdmin = container.getAttribute("data-isAdmin");

    console.log("userAdmin", userAdmin);

    setFormMode(mode);
    setUserTransitieagenda(userTID);
    setIsAdmin(userAdmin);

    if (mode === "edit" && nid !== "") {
      setValues((prev) => ({
        ...prev,
        nid: nid,
      }));

      getNodeData(nid);
    }

    // if (userTID && userTID.length > 0) {
    //   setValues((prev) => ({ ...prev, transitieagenda: [userTID] }));
    //   setTransitieagendaColor(userTColour)
    // }

    fetch("/api/taxonomy_terms/transitieagenda")
      .then((res) => res.json())
      .then((data) => {
        setTransitieagendaOptions(data);
      });

    fetch("/api/taxonomy_terms/inspiratiecategory")
      .then((res) => res.json())
      .then((data) => {
        setCategoryOptions(data);
      });

    fetch("/api/contactpersonen_filteroptions?team=canvas")
      .then((res) => res.json())
      .then((data) => {
        setContactPersonenOptions(data);
      });

    fetchOptions();
  }, []);

  useEffect(() => {
    if (isModalOpen) {
      document.body.classList.add('overflow-hidden');
    } else {
      document.body.classList.remove('overflow-hidden');
    }

    // Cleanup when component unmounts
    return () => {
      document.body.classList.remove('overflow-hidden');
    };
  }, [isModalOpen]);

  useEffect(() => {
    if (activeModalType) {
      document.body.classList.add('overflow-hidden');
    } else {
      document.body.classList.remove('overflow-hidden');
    }

    // Cleanup when component unmounts
    return () => {
      document.body.classList.remove('overflow-hidden');
    };
  }, [activeModalType]);

  useEffect(() => {
    if (activeModalType) {
      const fetchNodes = async () => {
        try {
          const csrfToken = await _getCSRFTokenValue();

          const res = await fetch('/api/inspiratiepunten_searchandfilter', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-CSRF-Token': csrfToken,
            },
            body: JSON.stringify({ type: [activeModalType], postsPerPage: 9999 }),
          });

          const data = await res.json();
          setAllResults(data.posts || []);
        } catch (err) {
          console.error("Fetch error:", err);
        }
      };

      fetchNodes();
    }
  }, [activeModalType]);

  useEffect(() => {
    const filtered = allResults
      .filter((item) =>
        item.title?.toLowerCase().includes(searchTerm.toLowerCase())
      )
      .map((item) => ({
        nid: item.nid,
        title: item.title,
        imageUrl: item.imageUrl,
        image_shape: item.image_shape,
        relaycolor: item.color,
        transitieagenda: item.transitieagendaLabel
      }));

    setSearchResults(filtered);
  }, [searchTerm, allResults]);

  // useEffect(() => {

  // }, []);

  const fetchOptions = async () => {
    api
      .get("/inspiratiepunten_filteroptions")
      .then((res) => {
        setIsLoadingPosts(false);

        // setOptions(res.data.options);
        const { project_type, project_status, thumbnail_shape, actueel_wijken } = res.data.options;

        setTypeOptions(project_type);
        setStatusOptions(project_status);
        setthumbnailOptions(thumbnail_shape);
        setWijkOptions(actueel_wijken);
      })
      .catch(() => {
        setIsLoadingPosts(false);
      });
  };

  const getNodeData = async (nid) => {
    setIsLoadingPosts(true);
    const csrfToken = await _getCSRFTokenValue();

    api
      .post(
        "/inspiratiepunten_node_data",
        { nid },
        {
          headers: {
            "X-CSRF-Token": csrfToken,
          },
        }
      )
      .then((res) => {
        setIsLoadingPosts(false);

        const parsedData = JSON.parse(res.data.data);

        console.log("parsedData", parsedData);
        setValues((prev) => ({
          ...prev,
          ...parsedData,
        }));

        setType(parsedData.type);
        setImagePreview(parsedData.imageUrl);
        setFileName(parsedData.mediaFileName);
        setMediaId(parsedData.mediaTargetId);
        setPartners(parsedData.partners);
        setLinks(parsedData.websites);
        setAddresses(parsedData.address);
        setSelectedCategory(parsedData.categories);
        setDocuments(parsedData.documents);
        // setTransitieagendaColor(parsedData.color);
        setPostStatus(parsedData.postStatus);

        setFlexiBlocks(parsedData.flexiBlocks || []);

        const allNids = parsedData.related_inspiratiepunten.map((item) => item.nid);

        setAddedItems((prev) => {
          const newItems = allNids.filter((nid) => !prev.includes(nid));
          return [...prev, ...newItems];
        });

        const groupedByType = parsedData.related_inspiratiepunten.reduce(
          (acc, item) => {
            if (!acc[item.type]) acc[item.type] = [];
            acc[item.type].push(item);
            return acc;
          },
          {}
        );

        setSelectedProjecten(groupedByType.projecten || []);
        setSelectedPersonen(groupedByType.personen || []);
        setSelectedMaterialen(groupedByType.materialen || []);


      })
      .catch(() => {
        setIsLoadingPosts(false);
      });
  };

  //image upload
  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    setFileName(file?.name || "");
    setImagePreview(URL.createObjectURL(file));
    setMediaId(null);

    if (!file) return;

    const uploadData = new FormData();
    uploadData.append("image", file);

    try {
      const res = await fetch("/api/upload_image", {
        method: "POST",
        body: uploadData,
      });

      const result = await res.json();

      if (res.ok && result.status === "success") {
        // Use the correct ID — media_id or file_id depending on your need
        setMediaId(result.file_id || result.media_id); // Adjust this based on your backend response
        setErrors((prev) => ({ ...prev, image: null }));
      } else {
        setErrors((prev) => ({
          ...prev,
          image: result.message || "Fout bij uploaden van afbeelding.",
        }));
      }
    } catch (err) {
      console.error("Image upload error:", err);
      setErrors((prev) => ({
        ...prev,
        image: "Afbeelding uploaden mislukt.",
      }));
    }
  };

  const handleRemoveImage = () => {
    setImagePreview(null);
    setFileName("");
    setMediaId(null);
    document.getElementById("projectImage").value = null; // clear input
  };

  //document upload
  const handleDocumentChange = async (e) => {
    const newFiles = Array.from(e.target.files);
    const uploadedDocs = [];

    for (const file of newFiles) {
      const uploadData = new FormData();
      uploadData.append("image", file);

      try {
        const res = await fetch("/api/upload_image", {
          method: "POST",
          body: uploadData,
        });

        const result = await res.json();

        if (res.ok && result.status === "success") {
          uploadedDocs.push({
            id: result.file_id || result.media_id,
            name: file.name,
            url: result.url || '',
          });
        } else {
          console.error("Upload failed:", result.message || "Unknown error");
        }
      } catch (err) {
        console.error("Document upload error:", err);
      }
    }

    // Merge with existing documents and deduplicate by name
    setDocuments((prev) => {
      const combined = [...prev, ...uploadedDocs];
      const seen = new Set();
      return combined.filter((doc) => {
        if (seen.has(doc.name)) return false;
        seen.add(doc.name);
        return true;
      });
    });

    e.target.value = '';
  };

  // Remove a single document by index
  const handleRemoveDocument = (indexToRemove) => {
    setDocuments((prev) => prev.filter((_, index) => index !== indexToRemove));
    document.getElementById("projectDocuments").value = null;
  };

  const handleAddLink = () => {
    if (!linkInput.url || !linkInput.text) return;

    setLinks([...links, linkInput]);
    setLinkInput({ url: '', text: '' }); // clear input
  };

  const removeLink = (index) => {
    setLinks(links.filter((_, i) => i !== index));
  };

  const handleAddPartner = () => {
    if (!partnerInput.trim()) return;

    setPartners([...partners, partnerInput.trim()]);
    setPartnerInput('');
  };

  const removePartner = (index) => {
    setPartners(partners.filter((_, i) => i !== index));
  };

  const removeWijk = (index) => {
    setValues({ ...values, wijk: values.wijk.filter((_, i) => i !== index) });
  };

  const handleAddAddress = () => {
    if (
      newAddress.name.trim() &&
      newAddress.street.trim() &&
      newAddress.postcode.trim() &&
      newAddress.city.trim() &&
      newAddress.lat.trim() &&
      newAddress.lng.trim()
    ) {
      setAddresses((prev) => [...prev, newAddress]);

      // Reset form
      setNewAddress({
        name: '',
        street: '',
        postcode: '',
        city: '',
        lat: '',
        lng: '',
      });

      setIsModalOpen(false);
    } else {
      setLocatieError({
        name: !newAddress.name.trim(),
        street: !newAddress.street.trim(),
        postcode: !newAddress.postcode.trim(),
        city: !newAddress.city.trim(),
        lat: !newAddress.lat.trim(),
        lng: !newAddress.lng.trim(),
      });
    }
  };

  const handleRemoveAddress = (index) => {
    setAddresses(addresses.filter((_, i) => i !== index));
  };

  const handleSelectInspiratiepunt = (item) => {
    const updater = {
      projecten: setSelectedProjecten,
      personen: setSelectedPersonen,
      materialen: setSelectedMaterialen,
    }[activeModalType];

    if (!updater) return;

    updater((prev) => {
      // If exists REMOVE it
      if (prev.find((p) => p.nid === item.nid)) {
        return prev.filter((p) => p.nid !== item.nid);
      }

      // If not exists ADD it
      return [...prev, item];
    });

    setAddedItems((prev) => {
      // If exists REMOVE nid
      if (prev.includes(item.nid)) {
        return prev.filter((id) => id !== item.nid);
      }

      // If not exists ADD nid
      return [...prev, item.nid];
    });
  };

  const saveNode = async (e) => {
    e.preventDefault();

    setLoading(true);
    setErrors({});
    setResponse(null);

    const formData = new FormData(e.target);
    const formValues = {};

    for (const [key, value] of formData.entries()) {
      if (formValues[key]) {
        if (!Array.isArray(formValues[key])) {
          formValues[key] = [formValues[key]];
        }
        formValues[key].push(value);
      } else {
        formValues[key] = value;
      }
    }

    const allSelected = [
      ...selectedProjecten,
      ...selectedPersonen,
      ...selectedMaterialen,
    ];

    if (values.nid) {
      formValues["nid"] = values.nid;
    }

    if (values.transitieagenda) {
      formValues["transitieagenda"] = values.transitieagenda;
    }

    if (values.wijk) {
      formValues["wijk"] = values.wijk;
    }

    // Attach uploaded media ID
    if (mediaId) {
      formValues["image"] = mediaId;
    }

    if (documents && documents.length) {
      formValues["documents"] = documents.map((doc) => doc.id);
    }
    else {
      formValues["documents"] = [];
    }

    if (links) {
      formValues["websites"] = links;
    }

    if (values.contactpersoon) {
      formValues["contactpersoon"] = values.contactpersoon;
    }

    if (partners) {
      formValues["partners"] = partners;
    }

    if (addresses) {
      formValues["locations"] = addresses;
    }

    if (allSelected) {
      formValues["related_inspiratiepunten"] = allSelected;
    }

    if (selectedCategory) {
      formValues["category_taxonomy"] = selectedCategory;
    }


    if (action === "concept") {
      formValues["field_post_status"] = 1;
    } else if (action === "forpublish") {
      formValues["field_post_status"] = 2;
    }
    else if (action === "publish") {
      formValues["field_post_status"] = 3;
    }

    formValues['body'] = values.body;
    formValues['flexible_content'] = flexiBlocks;

    try {
      const csrfToken = await _getCSRFTokenValue();

      const res = await fetch("/api/inspiratiepunten_node_save", {
        method: "POST",
        headers: {
          "X-CSRF-Token": csrfToken,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formValues),
      });

      const data = await res.json();

      if (res.ok && data.status === "success") {
        setResponse({
          status: "success",
          message: `Formulier verzonden. Node ID: ${data.nid}`,
        });

        // if (formMode == "create") {
        //   setValues(INITIAL_VALUES);
        //   setFileName("");
        //   setMediaId(null);
        //   setImagePreview(null);
        //   setPartners([]);
        //   setLinks([]);
        //   setAddresses([]);
        //   setSelectedProjecten([]);
        //   setSelectedPersonen([]);
        //   setSelectedMaterialen([]);
        //   setSelectedCategory([]);
        //   setDocuments([]);
        // }
        // else {
        //   window.location.reload();
        // }

        // let urlParams = '?message=' + values.title + ' is succesvol opgeslagen.';
        let urlParams = '?t=' + encodeURIComponent(values.title);

        if (isAdmin == 1) {
          window.location = "/praktijkervaring/admin-dashboard/" + urlParams;
        } else {
          window.location = "/praktijkervaring/dashboard/" + urlParams;
        }
      } else {
        setResponse({
          status: "error",
          message: data.message || "Er is iets misgegaan",
        });
        if (data.errors) setErrors(data.errors);
      }
    } catch (err) {
      console.error("Submit error", err);
      setResponse({
        status: "error",
        message: "Serverfout. Probeer opnieuw.",
      });
    }

    setLoading(false);
  };

  const searchGoogleMap = async () => {
    const searchAddress = newAddress.name + ", " + newAddress.street + ", " + newAddress.postcode + " " + newAddress.city;
    const address = searchAddress;
    const apiKey = "AIzaSyCIfQJ0OI1zgzIpKauDitSIqZIFLRqBuzQ";
    const encodedAddress = encodeURIComponent(address);

    let geocoder;

    geocoder = new google.maps.Geocoder();

    geocoder.geocode({ address: address }, (results, status) => {
      if (status === "OK") {
        const location = results[0].geometry.location;
        const latitude = location.lat();
        const longitude = location.lng();

        setNewAddress({
          ...newAddress,
          lat: latitude.toString(),
          lng: longitude.toString(),
          mapURL: `https://www.google.com/maps/search/?api=1&query=${latitude},${longitude}`
        });

        setLocatieError({
          ...locatieError,
          lat: false,
          lng: false,
        });
      } else {
        console.error("Geocode was not successful for the following reason: " + status);
      }
    });
  }

  const isTransitieagendaSelected = (termId) => {
    return values.transitieagenda && values.transitieagenda.includes(termId);
  }

  const createNewInspiratieCategory = async (termName) => {
    const uploadData = new FormData();
    uploadData.append("term_name", termName);

    try {
      const res = await fetch("/api/create_new_inspiratie_category", {
        method: "POST",
        body: uploadData,
      });

      const result = await res.json();

      if (res.ok && result.status === "success") {
        setCategoryOptions((prev) => [
          ...prev,
          { value: result.term_id, label: result.term_name }
        ]);
        setSelectedCategory((prev) => [
          ...prev,
          result.term_id
        ]);
        setNewCategoryError(null);
      } else {
        console.log("Error:", result.message);
        setNewCategoryError(result.message);
      }
    } catch (err) {
      console.log("err:", err);
    }
  };

  return (
    <React.Fragment>

      <form id="projectForm" onSubmit={saveNode} className="py-6">

        <div className="container">
          <h1 className="h3 font-sharpgrotesk text-black font-bold capitalize pb-4">Basis informatie</h1>

          {/* Title */}
          <div className="md:grid md:grid-cols-3 py-2">
            <label htmlFor="projectTitle" className="pr-4">Titel <span className="text-red-500">*</span></label>
            <div className="w-full col-span-1 col-start-2">
              <input
                type="text"
                name="title"
                id="projectTitle"
                className="w-full border p-2 !text-base"
                value={values.title}
                onChange={(e) => {
                  setValues({ ...values, title: e.target.value });
                  setErrors({ ...errors, title: null });
                }}
              />
            </div>
            {errors.title && <div className="text-red-600 col-span-3">{errors.title}</div>}
          </div>

          {/* Type */}
          <div className="md:grid md:grid-cols-3 py-2">
            <label htmlFor="projectType" className="pr-4">Type <span className="text-red-500">*</span></label>
            <div className="w-full relative col-span-1 col-start-2 select-arrow">
              <select id="projectType" name="type" value={values.type}
                onChange={(e) => {
                  const selected = e.target.value;
                  setValues({ ...values, type: selected });
                  setType(selected);
                  setErrors({ ...errors, type: null });
                  }}
                className="w-full border p-2 appearance-none select-arrow text-base bg-white force-white-select">
                <option value="">Selecteer type</option>
                {typeOptions.map((term) => (
                  <option key={term.value} value={term.value}>{term.label}</option>
                ))}
              </select>
            </div>
            {errors.type && <div className="text-red-600 col-span-3">{errors.type}</div>}
          </div>

          {/* Thema (previously Transitieagenda) */}
          <div className="md:grid md:grid-cols-3 py-2">
            <label htmlFor="" className="pr-4">Thema <span className="text-red-500">*</span></label>
            <div className="w-full relative col-span-1 col-start-2 flex flex-wrap gap-2">


              {values.transitieagenda.length > 0 ? (

                <DragDropContext onDragEnd={handleTransitieagendaDragEnd}>
                  <Droppable droppableId="TRANSITIEAGENDA" type="TRANSITIEAGENDA">
                    {(provided) => (
                      <div
                        {...provided.droppableProps}
                        ref={provided.innerRef}
                        style={{ padding: 16, background: "#f3f3f3", borderRadius: 8, width: '100%' }}
                      >


                        {
                          values.transitieagenda.length > 0 && (
                            values.transitieagenda.map((value, index) => {
                              const term = transitieagendaOptions.find((term) => term.value === value);

                              if (term) {
                                return <Draggable key={value} draggableId={value} index={index}>
                                  {(provided, snapshot) => (
                                    <div
                                      ref={provided.innerRef}
                                      {...provided.draggableProps}
                                      {...provided.dragHandleProps}
                                      style={{
                                        padding: "0.5rem 1rem",
                                        margin: "0.25rem",
                                        background: snapshot.isDragging ? "#c1d7ef" : term.color,
                                        borderRadius: 6,
                                        boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                                        backgroundColor: term.color,
                                        color: '#fff',
                                        ...provided.draggableProps.style,
                                      }}
                                    >

                                      <div className="flex items-center justify-start gap-2 relative">
                                        <img src={_getBaseUrl("src/assets/icons/icon-draggable.png", true)} alt="Drag" className="w-6 h-auto filter brightness-200" />
                                        <div className="!text-base">{term.label}</div>

                                        <img
                                          src={_getBaseUrl("src/assets/inspiratiepunten/remove.svg", true)}
                                          alt="Verwijder"
                                          className="absolute top-1/2 -translate-y-1/2 right-0 cursor-pointer"
                                          onClick={() => {
                                            let newSelected = [...values.transitieagenda];
                                            newSelected = newSelected.filter((t) => t !== term.value);
                                            setValues({ ...values, transitieagenda: newSelected });
                                          }}
                                        />
                                      </div>

                                    </div>
                                  )}
                                </Draggable>
                              }
                            })
                          )
                        }


                        {provided.placeholder}
                      </div>
                    )}
                  </Droppable>
                </DragDropContext>

              ) : (
                <div
                  style={{
                    padding: 12,
                    marginBottom: 8,
                    background: "#f3f3f3",
                    borderRadius: 6,
                    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                    width: '100%',
                  }}
                  className="!text-base"
                >Selecteer thema</div>
              )}




              <div className="flex flex-wrap gap-2 mb-8 mt-4">
                {
                  transitieagendaOptions.map((term) => {
                    if (!values.transitieagenda.includes(term.value)) {
                      return <button
                        class={`rounded-full leading-none px-3 py-2 cursor-pointer text-sm`}
                        style={{ backgroundColor: term.color, color: '#fff', border: 'none' }}
                        onClick={(e) => {
                          e.preventDefault();
                          let newSelected = [...values.transitieagenda];
                          if (newSelected.includes(term.value)) {
                            newSelected = newSelected.filter((t) => t !== term.value);
                          } else {
                            newSelected.push(term.value);
                            setErrors({ ...errors, transitieagenda: null });
                          }
                          setValues({ ...values, transitieagenda: newSelected });
                        }}
                      >{term.label}</button>
                    }
                  })
                }
              </div>
            </div>
            {errors.transitieagenda && <div className="text-red-600 col-span-3">{errors.transitieagenda}</div>}
          </div>

          {/* Image Shape */}
          <div className="md:grid md:grid-cols-3 py-2">
            <label htmlFor="projectImageshape" className="pr-4">Afbeelding vorm <span className="text-red-500">*</span></label>
            <div className="w-full relative col-span-1 col-start-2 select-arrow">
              <select id="projectImageshape" name="image_shape" value={values.image_shape}
                onChange={(e) => {
                  const selected = e.target.value;
                  setValues({ ...values, image_shape: selected });
                  setErrors({ ...errors, image_shape: null });
                }}
                className="w-full border p-2 appearance-none select-arrow text-base bg-white force-white-select">
                <option value="">Selecteer image shape</option>
                {thumbnailOptions.map((term) => (
                  <option key={term.value} value={term.value}>{term.label}</option>
                ))}
              </select>
            </div>
            {errors.image_shape && <div className="text-red-600 col-span-3">{errors.image_shape}</div>}
          </div>

          {/* Image Upload */}
          <div className="md:grid md:grid-cols-3 py-4">
            <label htmlFor="projectImage" className="pr-4">Afbeelding <span className="text-red-500">*</span></label>
            <div className="w-full col-span-2 flex flex-wrap items-center gap-4">
              {imagePreview && (
                <div className="relative">

                  <div className="aspect-square relative" style={{ width: "125px", height: "125px" }}>
                    <div className="absolute bottom-1 -right-2 z-0">
                      {_getTileShape(
                        values.image_shape,
                        values.transitieagenda.length > 0 ?
                          transitieagendaOptions.find((ta) => ta.value === values.transitieagenda[0])?.color : "#000",
                        "125")}
                    </div>
                    <img
                      src={imagePreview}
                      alt="Voorbeeld"
                      className={`list-thumbnail w-full h-full object-cover relative z-10 clip-path-${values.image_shape}`}
                    />
                  </div>

                  <button
                    type="button"
                    onClick={handleRemoveImage}
                    className="hover:opacity-80"
                  >
                    <img
                      src={_getBaseUrl("src/assets/inspiratiepunten/remove.svg", true)}
                      alt="Verwijder"
                      className="absolute top-1 -right-6 w-5 h-5 z-10"
                    />
                  </button>
                </div>
              )}
              <span className="text-sm text-gray-700">{fileName}</span>
              <button
                type="button"
                onClick={() => document.getElementById("projectImage").click()}
                className="text-base rounded-full py-3 px-5 border border-black cursor-pointer whitespace-nowrap"
              >
                Upload afbeelding
              </button>
              <input
                type="file"
                name="actualimage"
                id="projectImage"
                className="hidden w-fit"
                onChange={handleFileChange}
              />
            </div>
            {errors.image && <div className="text-red-600 col-span-3">{errors.image}</div>}
          </div>

          {imagePreview && (
            <div className="md:grid md:grid-cols-3 py-2">
              <label htmlFor="projectImageAlt" className="pr-4">Image Alt <span className="text-red-500">*</span></label>
              <div className="w-full col-span-1 col-start-2">
                <input
                  type="text"
                  name="image_alt"
                  id="projectImageAlt"
                  className="w-full border p-2 !text-base"
                  value={values.image_alt}
                  onChange={(e) => {
                    setValues({ ...values, image_alt: e.target.value });
                    setErrors({ ...errors, image_alt: null });
                  }}
                />
              </div>
              {errors.image_alt && (
                <div className="text-red-600 col-span-3">{errors.image_alt}</div>
              )}
            </div>
          )}

          {/* Jaar */}
          {(type === "projecten") && (
            <div className="md:grid md:grid-cols-3 py-2">
              <label htmlFor="projectJaar" className="pr-4">Jaar</label>
              <div className="w-full col-span-1 col-start-2">
                <input type="text" name="jaar" id="projectJaar" className="w-full border p-2 !text-base"
                  value={values.jaar}
                  onChange={(e) =>
                    setValues({ ...values, jaar: e.target.value })
                  } />
              </div>
            </div>
          )}

          {/* Wijk */}
          {(type === "projecten") && (
            <div className="md:grid md:grid-cols-3 py-2">
              <label htmlFor="projectImageshape" className="pr-4">Wijk</label>
              <div className="w-full relative col-span-1 col-start-2">



                { values.wijk.length > 0 && (
                  <ul className="text-base list-disc flex flex-wrap gap-x-4 my-2">
                    {values.wijk.map((id, index) => (
                      <li key={index} className="flex items-center gap-2 w-fit">
                        {wijkOptions.find((term) => term.value === id)?.label || id}
                        <button
                          type="button"
                          onClick={() => removeWijk(index)}
                          className="hover:opacity-80"
                        >
                          <img
                            src={_getBaseUrl("src/assets/inspiratiepunten/remove.svg", true)}
                            alt="Verwijder"
                            className="w-4 h-4"
                          />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}



                {/* <div className="relative select-arrow">
                  <select id="wijk" name="wijk" value={values.wijk}
                    onChange={(e) => {
                      const selected = e.target.value;
                      setValues({ ...values, wijk: [selected] });
                    }}
                    className="w-full border p-2 appearance-none select-arrow text-base bg-white force-white-select">
                    <option value="">Selecteer wijk</option>
                    {wijkOptions.map((term) => (
                      <option key={term.value} value={term.value}>{term.label}</option>
                    ))}
                  </select>
                </div> */}

                <div className="relative select-arrow mt-4">
                  <MultiSelectDropdown
                    options={wijkOptions}
                    selected={values.wijk}
                    setSelected={(selected) => setValues({ ...values, wijk: selected })}
                    classes="text-base"
                    searchable={true}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Status */}
          {(type === "projecten") && (
            <div className="md:grid md:grid-cols-3 py-4">
              <label htmlFor="projectStatus" className="pr-4">Status <span className="text-red-500">*</span></label>
              <div className="w-full relative col-span-1 col-start-2 select-arrow">
                <select id="projectStatus" name="project_status" value={values.projectStatus} className="w-full border p-2 appearance-none select-arrow text-base bg-white force-white-select"
                  onChange={(e) => {
                    const selectedValue = e.target.value;
                    const selectedTerm = statusOptions.find(term => term.value === selectedValue);
                    setValues({ ...values, projectStatus: selectedValue });
                    setErrors({ ...errors, project_status: null });
                  }}>
                  <option value="">Selecteer status</option>
                  {statusOptions.map((term) => (
                    <option key={term.value} value={term.value}>{term.label}</option>
                  ))}
                </select>
              </div>
              {errors.project_status && <div className="text-red-600 col-span-3">{errors.project_status}</div>}
            </div>
          )}

          {/* Functie */}
          {type === "personen" && (
            <div className="md:grid md:grid-cols-3 py-2">
              <label htmlFor="projectFunctie" className="pr-4">Functie</label>
              <div className="w-full col-span-1 col-start-2">
                <input type="text" name="functie" id="projectFunctie" className="w-full border p-2 !text-base"
                  value={values.functie}
                  onChange={(e) =>
                    setValues({ ...values, functie: e.target.value })
                  } />
              </div>
            </div>
          )}

          {/* Partner*/}
          {(type === "projecten") && (
            <div className="md:grid md:grid-cols-3 py-2">
              <label htmlFor="partner-name" className="pr-4">Partners</label>
              <div className="w-full col-span-2 col-start-2">

                <ul className="text-base list-disc flex flex-wrap gap-x-4 my-2">
                  {partners.map((name, index) => (
                    <li key={index} className="flex items-center gap-2 w-fit">
                      {name}
                      <button
                        type="button"
                        onClick={() => removePartner(index)}
                        className="hover:opacity-80"
                      >
                        <img
                          src={_getBaseUrl("src/assets/inspiratiepunten/remove.svg", true)}
                          alt="Verwijder"
                          className="w-4 h-4"
                        />
                      </button>
                    </li>
                  ))}
                </ul>

                {/* Input and Add Button */}
                <div className="flex flex-col md:flex-row gap-4">

                  <div className="flex flex-col w-full flex-grow">
                    <input
                      type="text"
                      id="partner-name"
                      placeholder=""
                      value={partnerInput}
                      onChange={(e) => setPartnerInput(e.target.value)}
                      className="border p-2 rounded-md !text-base"
                    />
                  </div>

                  <div className="w-fit flex-shrink-0">
                    <button
                      type="button"
                      onClick={handleAddPartner}
                      className="text-base rounded-full py-3 px-5 border border-black cursor-pointer mt-5 md:mt-0"
                    >
                      + Toevoegen
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Websites */}
          {(type === "projecten" || type === "personen") && (
            <div className="md:grid md:grid-cols-3 py-2">
              <label className="pr-4">Website(s)</label>
              <div className="w-full col-span-2 flex flex-col gap-2">

                <ul className="text-base list-disc my-2">
                  {links.map((link, index) => (
                    <li key={index} className="flex items-center gap-2">
                      {link.text} - {link.url}

                      <button
                        type="button"
                        onClick={() => removeLink(index)}
                        className="hover:opacity-80"
                      >
                        <img
                          src={_getBaseUrl("src/assets/inspiratiepunten/remove.svg", true)}
                          alt="Verwijder"
                          className="w-4 h-4"
                        />
                      </button>
                    </li>
                  ))}
                </ul>

                <div className="flex flex-col xl:flex-row gap-2 border border-[#CCC] rounded-md p-4">
                  {/* Link URL */}
                  <div className="flex flex-col w-full xl:w-2/5">
                    <label htmlFor="link-url" className="text-sm font-normal mb-1">Link</label>
                    <input
                      type="url"
                      id="link-url"
                      placeholder=""
                      value={linkInput.url}
                      onChange={(e) => setLinkInput({ ...linkInput, url: e.target.value })}
                      className="border p-2 rounded-md !text-base"
                    />
                  </div>

                  {/* Link Text */}
                  <div className="flex flex-col w-full xl:w-2/5">
                    <label htmlFor="link-text" className="text-sm mb-1">Button tekst</label>
                    <input
                      type="text"
                      id="link-text"
                      placeholder=""
                      value={linkInput.text}
                      onChange={(e) => setLinkInput({ ...linkInput, text: e.target.value })}
                      className="border p-2 rounded-md !text-base"
                    />
                  </div>

                  {/* Add Button */}
                  <div className="flex items-end">
                    <button
                      type="button"
                      onClick={handleAddLink}
                      className="text-base rounded-full py-3 px-5 border border-black cursor-pointer mt-5 xl:mt-0"
                    >
                      + Toevoegen
                    </button>
                  </div>
                </div>

              </div>
            </div>
          )}








          { contactPersonenOptions.length > 0 && (
            <div className="md:grid md:grid-cols-3 py-2">
              <label htmlFor="contactpersoon" className="pr-4">Contactpersoon</label>
              <div className="w-full relative col-span-1 col-start-2 select-arrow">
                <select id="contactpersoon" name="contactpersoon" value={values.contactpersoon}
                  onChange={(e) => {
                    const selected = e.target.value;
                    setValues({ ...values, contactpersoon: selected });
                    setErrors({ ...errors, contactpersoon: null });
                  }}
                  className="w-full border p-2 appearance-none select-arrow text-base bg-white force-white-select">
                  <option value="">Selecteer contactpersoon</option>
                  {contactPersonenOptions.map((contact) => (
                    <option key={contact.nid} value={contact.nid}>{contact.title}</option>
                  ))}
                </select>
              </div>
              {errors.contactpersoon && <div className="text-red-600 col-span-3">{errors.contactpersoon}</div>}
            </div>
          )}










          {/* Summary */}
          <div className="md:grid md:grid-cols-3 py-4">
            <label htmlFor="agendaSummary" className="pr-4">Samenvatting</label>
            <div className="w-full col-span-2">
              <textarea name="summary" id="agendaSummary" rows="2" className="w-full border p-2 !text-base"
                value={values.summary}
                onChange={(e) =>
                  setValues({ ...values, summary: e.target.value })
                }
              ></textarea>
            </div>
          </div>

          {/* Body */}
          {(type === "projecten") && (
            <div className="md:grid md:grid-cols-3 py-4">
              <label htmlFor="agendaBody" className="pr-4">Projectbeschrijving</label>
              <div className="w-full col-span-2">
                {/* <textarea name="body" id="agendaBody" rows="4" className="w-full border p-2 !text-base"
                  value={values.body}
                  onChange={(e) =>
                    setValues({ ...values, body: e.target.value })
                  }
                ></textarea> */}

                <div className="inspiratiepunten-ckeditor detail-text">
                  <CKEditor
                    editor={ClassicEditor}
                    config={{
                      licenseKey: 'GPL',
                      plugins: [Essentials, Paragraph, Bold, Italic, Heading, Link, List, BlockQuote, SourceEditing],
                      toolbar: ['undo', 'redo', '|', 'heading', '|', 'bold', 'italic', '|', 'link', 'bulletedList', 'numberedList', 'blockQuote', '|', 'sourceEditing'],
                      initialData: values.data,
                      height: '5rem',
                    }}
                    data={values.body}
                    onChange={(_, editor) => {
                      const data = editor.getData();
                      setValues((prev) => ({ ...prev, body: data }));
                    }}
                  />
                </div>

              </div>
            </div>
          )}

          {/* Flexible Content */}
          {(type === "projecten") && (
            <div className="md:grid md:grid-cols-3 py-4">
              <label className="pr-4">Extra Content</label>
              <div className="w-full col-span-2">

                {flexiBlocks.length === 0 ? (

                  <div
                    style={{
                      padding: 12,
                      marginBottom: 8,
                      background: "#f3f3f3",
                      borderRadius: 6,
                      boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                    }}
                    className="!text-base"
                  >Voeg hier content toe</div>
                ) : (

                  <DragDropContext onDragEnd={handleFlexiBlocksDragEnd}>
                    <Droppable droppableId="PARENTS" type="PARENT">
                      {(provided) => (
                        <div
                          {...provided.droppableProps}
                          ref={provided.innerRef}
                          style={{ padding: 16, background: "#f3f3f3", borderRadius: 8 }}
                        >

                          <div className="flex justify-end gap-2 mb-3">
                            <button type="button"
                              onClick={(e) => {
                                e.preventDefault();
                                setFlexiBlocks((prev) =>
                                  prev.map((field) => ({ ...field, expanded: false }))
                                );
                              }}
                              class="text-base rounded-full py-3 px-5 border border-black cursor-pointer">Alles Inklappen</button>
                            <button type="button"
                              onClick={(e) => {
                                e.preventDefault();
                                setFlexiBlocks((prev) =>
                                  prev.map((field) => ({ ...field, expanded: true }))
                                );
                              }}
                              class="text-base rounded-full py-3 px-5 border border-black cursor-pointer">Alles uitklappen</button>
                          </div>

                          {flexiBlocks.map((field, index) => (
                            <Draggable key={field.id} draggableId={field.id} index={index}>
                              {(provided, snapshot) => (
                                <div
                                  ref={provided.innerRef}
                                  {...provided.draggableProps}
                                  {...provided.dragHandleProps}
                                  style={{
                                    padding: 12,
                                    marginBottom: 8,
                                    background: snapshot.isDragging ? "#d1e7ff" : "white",
                                    borderRadius: 6,
                                    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                                    ...provided.draggableProps.style,
                                  }}
                                >
                                  <div className="flex justify-between items-center">
                                    <div className="flex items-center justify-start gap-2">
                                      <img src={_getBaseUrl("src/assets/icons/icon-draggable.png", true)} alt="Drag" className="w-6 h-auto" />
                                      <label htmlFor="">{FLEXI_BLOCKS[field.type].label}</label>
                                    </div>
                                    <button className="leading-none border border-cirBlack aspect-square flex-shrink-0 flex items-center justify-center rounded-full w-6 cursor-pointer" onClick={(e) => {
                                      e.preventDefault();
                                      toggleExpandField(field.id);
                                    }}>
                                      {field.expanded ? "−" : "+"}
                                    </button>
                                  </div>

                                  {/* Field Content */}
                                  {field.expanded ? (
                                    <div className="inspiratiepunten-ckeditor detail-text mt-2 overflow-x-auto">
                                      {/* Rich text */}
                                      {field.type === "text_content" && (
                                        <div className="grid gap-2">
                                          <CKEditor
                                            editor={ClassicEditor}
                                            config={{
                                              licenseKey: 'GPL',
                                              plugins: [Essentials, Paragraph, Bold, Italic, Heading, Link, List, BlockQuote, SourceEditing],
                                              toolbar: ['undo', 'redo', '|', 'heading', '|', 'bold', 'italic', '|', 'link', 'bulletedList', 'numberedList', 'blockQuote', '|', 'sourceEditing'],
                                              initialData: field.value,
                                              height: '5rem',
                                            }}
                                            data={field.value}
                                            onChange={(_, editor) => {
                                              const data = editor.getData();
                                              setFlexiBlocks((prev) =>
                                                prev.map((f) =>
                                                  f.id === field.id ? { ...f, value: data } : f
                                                )
                                              );
                                            }}
                                          />
                                          <div className="flex justify-end">
                                            <button
                                              type="button"
                                              class="flex-shrink-0 text-base rounded-full py-3 px-5 border bg-red-700 text-white cursor-pointer w-fit"
                                              onClick={() => removeFlexiField(field.id)}
                                            >Verwijderen</button>
                                          </div>
                                        </div>
                                      )}

                                      {/* Image carousel */}
                                      {field.type === "photo_gallery" && (
                                        <div className="grid grid-cols-1 gap-4 mt-4">

                                          {field.value.length > 0 && (
                                            // Draggable for image items
                                            <Droppable droppableId={`children-${field.id}`} type={field.id}>
                                              {(provided) => (
                                                <div
                                                  {...provided.droppableProps}
                                                  ref={provided.innerRef}
                                                  style={{ padding: 16, background: "#f3f3f3", borderRadius: 8 }}
                                                >
                                                  {field.value.map((item, idx) => (
                                                    <Draggable key={item.id} draggableId={item.id} index={idx}>
                                                      {(provided, snapshot) => (
                                                        <div
                                                          ref={provided.innerRef}
                                                          {...provided.draggableProps}
                                                          {...provided.dragHandleProps}
                                                          style={{
                                                            padding: 12,
                                                            marginBottom: 8,
                                                            background: snapshot.isDragging ? "#d1e7ff" : "white",
                                                            borderRadius: 6,
                                                            boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                                                            ...provided.draggableProps.style,
                                                          }}
                                                        >
                                                          <div key={idx} className="flex justify-center bg-gray-100 p-3 rounded-md">

                                                            {item.mediaId !== null && (
                                                              <div className="grid grid-cols-12 gap-4">
                                                                <div className="col-span-1 flex items-center justify-center">
                                                                  <img src={_getBaseUrl("src/assets/icons/icon-draggable.png", true)} alt="Drag" className="w-6 h-auto" />
                                                                </div>
                                                                <div className="col-span-2">
                                                                  <img src={item.imgUrl} alt="" className="aspect-square object-cover" />
                                                                </div>
                                                                <div className="col-span-9 flex items-center">
                                                                  <div className="w-full flex gap-2">
                                                                    <div className="flex flex-col w-full">
                                                                      <label htmlFor={`alt-text-${index}-${idx}`} className="text-sm mb-1">Alt text</label>
                                                                      <input
                                                                        type="text"
                                                                        id={`alt-text-${index}-${idx}`}
                                                                        placeholder=""
                                                                        value={item.altText}
                                                                        onChange={(e) => {
                                                                          const newAltText = e.target.value;
                                                                          setFlexiBlocks((prev) =>
                                                                            prev.map((f) => {
                                                                              if (f.id === field.id) {
                                                                                const newValue = f.value.map((valItem) => {
                                                                                  if (valItem.id === item.id) {
                                                                                    return { ...valItem, altText: newAltText };
                                                                                  }
                                                                                  return valItem;
                                                                                });
                                                                                return { ...f, value: newValue };
                                                                              }
                                                                              return f;
                                                                            })
                                                                          );
                                                                        }}
                                                                        className="border p-2 rounded-md !text-base"
                                                                      />
                                                                    </div>
                                                                    <div className="flex-shrink-0 flex flex-col justify-end">
                                                                      <button
                                                                        type="button"
                                                                        class="flex-shrink-0 text-sm rounded-full py-2 px-3 border bg-red-700 text-white cursor-pointer w-fit"
                                                                        onClick={() => removeFromImageGallery(field.id, item.id)}
                                                                      >Verwijderen</button>
                                                                    </div>
                                                                  </div>
                                                                </div>
                                                              </div>
                                                            )}
                                                          </div>
                                                        </div>
                                                      )}
                                                    </Draggable>
                                                  ))}
                                                  {provided.placeholder}
                                                </div>
                                              )}
                                            </Droppable>
                                          )}
                                          <div className="flex justify-end gap-2">
                                            <button
                                              type="button"
                                              onClick={() => document.getElementById('media-image-file' + field.id).click()}
                                              className="text-base rounded-full py-3 px-5 border border-black cursor-pointer"
                                            >
                                              Upload afbeelding
                                            </button>
                                            <input
                                              type="file"
                                              name="mediaimage"
                                              id={'media-image-file' + field.id}
                                              className="hidden w-fit"
                                              onChange={(e) => mediaImageUpload(e, field.id)}
                                            />
                                            <button
                                              type="button"
                                              class="flex-shrink-0 text-base rounded-full py-3 px-5 border bg-red-700 text-white cursor-pointer w-fit"
                                              onClick={() => removeFlexiField(field.id)}
                                            >Verwijderen</button>
                                          </div>
                                        </div>
                                      )}

                                      {/* Video */}
                                      {field.type === "video" && (
                                        <div className="grid gap-2 px-3 mt-2">
                                          <div className="flex flex-col w-full gap-2">
                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                              <div className="flex flex-col w-full">
                                                <label htmlFor={`video-id-${index}`} className="text-sm mb-1">Video ID</label>
                                                <input
                                                  type="text"
                                                  id={`video-id-${index}`}
                                                  placeholder=""
                                                  value={field.value.videoId}
                                                  onChange={(e) => {
                                                    const newVideoId = e.target.value;
                                                    setFlexiBlocks((prev) =>
                                                      prev.map((f) =>
                                                        f.id === field.id ? { ...f, value: { ...f.value, videoId: newVideoId } } : f
                                                      )
                                                    );
                                                  }}
                                                  className="border p-2 rounded-md !text-base"
                                                />
                                              </div>
                                              <div className="flex flex-col w-full">
                                                <label className="text-sm mb-1">Video Type</label>
                                                <div className="flex items-center justify-start gap-4" style={{ height: '45px' }}>
                                                  <div
                                                    onClick={(e) => {
                                                      setFlexiBlocks((prev) =>
                                                        prev.map((f) =>
                                                          f.id === field.id ? { ...f, value: { ...f.value, videoType: "youtube" } } : f
                                                        )
                                                      );
                                                    }}
                                                    className="flex items-center justify-start gap-2 cursor-pointer">
                                                    <button 
                                                      onClick={(e) => {
                                                        e.preventDefault();
                                                      }}
                                                      className="aspect-square rounded-full w-6 border-2 p-1 border-cirGrey">
                                                      {field.value.videoType === "youtube" && (
                                                        <div className="aspect-square rounded-full w-full bg-cirGold"></div>
                                                      )}
                                                    </button>
                                                    <label htmlFor="" className="text-sm leading-none cursor-pointer">Youtube</label>
                                                  </div>
                                                  <div
                                                    onClick={(e) => {
                                                      setFlexiBlocks((prev) =>
                                                        prev.map((f) =>
                                                          f.id === field.id ? { ...f, value: { ...f.value, videoType: "vimeo" } } : f
                                                        )
                                                      );
                                                    }}
                                                    className="flex items-center justify-start gap-2 cursor-pointer">
                                                    <button 
                                                      onClick={(e) => {
                                                        e.preventDefault();
                                                      }}
                                                      className="aspect-square rounded-full w-6 border-2 p-1 border-cirGrey">
                                                      {field.value.videoType === "vimeo" && (
                                                        <div className="aspect-square rounded-full w-full bg-cirGold"></div>
                                                      )}
                                                    </button>
                                                    <label htmlFor="" className="text-sm leading-none cursor-pointer">Vimeo</label>
                                                  </div>
                                                </div>
                                              </div>
                                            </div>
                                          </div>
                                          <div className="flex flex-col w-full gap-2">
                                            <label htmlFor={`video-title-${index}`} className="text-sm mb-1">Video Titel</label>
                                            <input
                                              type="text"
                                              id={`video-title-${index}`}
                                              placeholder=""
                                              value={field.value.videoTitle}
                                              onChange={(e) => {
                                                const newVideoId = e.target.value;
                                                setFlexiBlocks((prev) =>
                                                  prev.map((f) =>
                                                    f.id === field.id ? { ...f, value: { ...f.value, videoTitle: newVideoId } } : f
                                                  )
                                                );
                                              }}
                                              className="border p-2 rounded-md !text-base"
                                            />
                                          </div>
                                          <div className="flex justify-end">
                                            <button
                                              type="button"
                                              class="flex-shrink-0 text-base rounded-full py-3 px-5 border bg-red-700 text-white cursor-pointer w-fit"
                                              onClick={() => removeFlexiField(field.id)}
                                            >Verwijderen</button>
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  ) : null}
                                </div>
                              )}
                            </Draggable>
                          ))}
                          {provided.placeholder}
                        </div>
                      )}
                    </Droppable>
                  </DragDropContext>
                )}

                <div className="pt-4 flex flex-wrap justify-start gap-2">
                  {Object.entries(FLEXI_BLOCKS).map(([type, { label }]) => (
                    <button
                      type="button"
                      class="flex-shrink-0 text-base rounded-full py-3 px-5 border border-black cursor-pointer w-fit"
                      onClick={() => addFlexiField(type)}
                    >+ {label}</button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* LOCATION */}
          {(type === "projecten") && (
            <div className="md:grid md:grid-cols-3 py-4">
              <label className="special-label">Locatie</label>
              <div className="col-span-2 flex flex-col gap-4">

                {/* Preview List */}
                {addresses.length > 0 && (
                  <ul className="flex flex-wrap gap-x-4">
                    {addresses.map((addr, index) => (
                      <li
                        key={index}
                        className="border border-black rounded-md p-4 text-sm flex gap-6 items-start w-fit mt-0"
                      >
                        <div className="flex gap-2">
                          <img
                            src={_getBaseUrl("src/assets/inspiratiepunten/location.svg", true)}
                            alt="Verwijder"
                            className="w-6 h-6 pt-1"
                          />

                          <div className="whitespace-pre-line text-base">
                            {addr.name && <>{addr.name}<br /></>}
                            {addr.street && <>{addr.street}<br /></>}
                            {(addr.postcode || addr.city) && (
                              <>
                                {addr.postcode} {addr.city}<br />
                              </>
                            )}
                            {/* {(addr.lat && addr.lng) && (
                            <span className="text-gray-500 text-xs">
                              Coördinaten: {addr.lat}, {addr.lng}
                            </span>
                          )} */}
                          </div>

                        </div>


                        <button
                          type="button"
                          onClick={() => handleRemoveAddress(index)}
                          className="hover:opacity-80"
                        >
                          <img
                            src={_getBaseUrl("src/assets/inspiratiepunten/remove.svg", true)}
                            alt="Verwijder"
                            className="w-4 h-4"
                          />
                        </button>
                      </li>
                    ))}
                  </ul>

                )}

                {/* Add Address Button */}
                <button
                  type="button"
                  onClick={() => setIsModalOpen(true)}
                  className="text-base rounded-full py-3 px-5 border border-black cursor-pointer w-fit"
                >
                  + Locatie toevoegen
                </button>
              </div>
            </div>
          )}

          {isModalOpen && (
            <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50">
              <div className="bg-white rounded-md shadow-lg w-full max-w-lg py-12 px-16 space-y-4 relative">
                <h2 className="h3 text-black font-semibold pb-4 font-sharpgrotesk">Locatie toevoegen</h2>

                {/* Locatie naam */}
                <TextInput
                  name="location-name"
                  label="Locatie naam"
                  placeholder=""
                  value={newAddress.name}
                  onChange={(value) => {
                    setNewAddress({ ...newAddress, name: value });
                    setLocatieError({ ...locatieError, name: false });
                  }}
                  error={locatieError.name}
                  marginCss=""
                />

                {/* Locatie naam */}
                {/* <div className="flex flex-col">
                  <label htmlFor="location-name" className="text-sm font-medium mb-1">Locatie naam</label>
                  <input
                    type="text"
                    id="location-name"
                    placeholder=""
                    value={newAddress.name}
                    onChange={(e) => setNewAddress({ ...newAddress, name: e.target.value })}
                    className="w-full border p-2 rounded"
                  />
                </div> */}

                {/* Adres */}
                {/* <div className="flex flex-col">
                  <label htmlFor="street" className="text-sm font-medium mb-1">Adres</label>
                  <input
                    type="text"
                    id="street"
                    placeholder=""
                    value={newAddress.street}
                    onChange={(e) => setNewAddress({ ...newAddress, street: e.target.value })}
                    className="w-full border p-2 rounded"
                  />
                </div> */}

                {/* Adres */}
                <TextInput
                  name="street"
                  label="Adres"
                  placeholder=""
                  value={newAddress.street}
                  onChange={(value) => {
                    setNewAddress({ ...newAddress, street: value });
                    setLocatieError({ ...locatieError, street: false });
                  }}
                  error={locatieError.street}
                  marginCss=""
                />

                <div className="grid grid-cols-2 gap-4">
                  {/* Postcode */}
                  {/* <div className="flex flex-col col-span-1">
                    <label htmlFor="postcode" className="text-sm font-medium mb-1">Postcode</label>
                    <input
                      type="text"
                      id="postcode"
                      placeholder=""
                      value={newAddress.postcode}
                      onChange={(e) => setNewAddress({ ...newAddress, postcode: e.target.value })}
                      className="w-full border p-2 rounded"
                    />
                  </div> */}

                  {/* Postcode */}
                  <TextInput
                    name="postcode"
                    label="Postcode"
                    placeholder=""
                    value={newAddress.postcode}
                    onChange={(value) => {
                      setNewAddress({ ...newAddress, postcode: value });
                      setLocatieError({ ...locatieError, postcode: false });
                    }}
                    error={locatieError.postcode}
                    marginCss=""
                  />

                  {/* Plaats */}
                  {/* <div className="flex flex-col col-span-2">
                    <label htmlFor="city" className="text-sm font-medium mb-1">Plaats</label>
                    <input
                      type="text"
                      id="city"
                      placeholder=""
                      value={newAddress.city}
                      onChange={(e) => setNewAddress({ ...newAddress, city: e.target.value })}
                      className="w-full border p-2 rounded"
                    />
                  </div> */}

                  {/* Plaats */}
                  <TextInput
                    name="city"
                    label="Plaats"
                    placeholder=""
                    value={newAddress.city}
                    onChange={(value) => {
                      setNewAddress({ ...newAddress, city: value });
                      setLocatieError({ ...locatieError, city: false });
                    }}
                    error={locatieError.city}
                    marginCss=""
                  />
                </div>

                {/* Coördinaten (lat + lng) */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex flex-col">
                    {/* <label htmlFor="lat" className="text-sm font-medium mb-1">Latitude</label>
                    <input
                      type="text"
                      id="lat"
                      placeholder=""
                      value={newAddress.lat}
                      onChange={(e) => setNewAddress({ ...newAddress, lat: e.target.value })}
                      className="w-full border p-2 rounded"
                    /> */}

                    {/* Latitude */}
                    <TextInput
                      name="lat"
                      label="Latitude"
                      placeholder=""
                      value={newAddress.lat}
                      onChange={(value) => {
                        setNewAddress({ ...newAddress, lat: value });
                        setLocatieError({ ...locatieError, lat: false });
                      }}
                      error={locatieError.lat}
                      marginCss=""
                    />
                    
                  </div>

                  <div className="flex flex-col">
                    {/* <label htmlFor="lng" className="text-sm font-medium mb-1">Longitude</label>
                    <input
                      type="text"
                      id="lng"
                      placeholder=""
                      value={newAddress.lng}
                      onChange={(e) => setNewAddress({ ...newAddress, lng: e.target.value })}
                      className="w-full border p-2 rounded"
                    /> */}

                    {/* Longitude */}
                    <TextInput
                      name="lng"
                      label="Longitude"
                      placeholder=""
                      value={newAddress.lng }
                      onChange={(value) => {
                        setNewAddress({ ...newAddress, lng: value });
                        setLocatieError({ ...locatieError, lng: false });
                      }}
                      error={locatieError.lng}
                      marginCss=""
                    />
                  </div>
                </div>

                <div className="flex gap-2 flex-wrap whitespace-nowrap">
                  <span
                    onClick={searchGoogleMap}
                    className="text-sm text-blue-600 underline cursor-pointer hover:text-blue-900"
                  >Vraag coördinaten aan</span>
                  {newAddress.mapURL && (
                    <a href={newAddress.mapURL} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 underline hover:text-blue-900">Bekijk op Google Maps</a>
                  )}
                </div>

                {Object.values(locatieError).some(val => val === true) && (
                  <div className="text-base text-red-500">Vul alle verplichte velden in.</div>
                )}

                {/* Buttons */}
                <div className="flex justify-start gap-2 pt-4">
                  <button
                    type="button"
                    onClick={() => setIsModalOpen(false)}
                    className="text-base rounded-full py-3 px-5 border text-black border-black cursor-pointer w-fit"
                  >
                    Annuleren
                  </button>
                  <button
                    type="button"
                    onClick={handleAddAddress}
                    className="text-base rounded-full py-3 px-5 border border-black bg-black text-white cursor-pointer w-fit"
                  >
                    Toevoegen
                  </button>
                </div>

                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="hover:opacity-80 absolute top-4 right-6"
                >
                  <img
                    src={_getBaseUrl("src/assets/inspiratiepunten/close.svg", true)}
                    alt="Verwijder"
                    className="w-4 h-4"
                  />
                </button>
              </div>
            </div>
          )}

          {/* RELATED NODES */}
          <div className="md:grid md:grid-cols-3 py-8">
            <label className="special-label">Ontdek meer</label>
            <div className="col-span-2 flex flex-col gap-4 pb-8">
              <div className="flex flex-col">
                <label className="special-font pb-6">Gerelateerde projecten</label>
                {selectedProjecten.length > 0 && (
                  <div className="flex flex-wrap gap-4 pb-4">
                    {selectedProjecten.map((item) => (
                      <div key={item.nid} className="flex items-center gap-2">
                        <div className="flex items-center gap-4">
                          <div className="h-[64px] w-[64px] aspect-square relative">
                            <div className="absolute bottom-1 -right-1 z-0">
                              {_getTileShape(item.image_shape, item.relaycolor, "64")}
                            </div>
                            <img
                              src={item.imageUrl}
                              alt={item.title}
                              className={`list-thumbnail w-full h-full object-cover relative z-10 clip-path-${item.image_shape}`}
                            />
                          </div>

                          <span className="!text-base">{item.title}</span>
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            setSelectedProjecten((prev) => prev.filter((p) => p.nid !== item.nid));
                            setAddedItems((prev) => prev.filter((nid) => nid !== item.nid));
                          }}
                          className="hover:opacity-80"
                        >
                          <img
                            src={_getBaseUrl("src/assets/inspiratiepunten/remove.svg", true)}
                            alt="Verwijder"
                            className="w-4 h-4"
                          />
                        </button>

                      </div>
                    ))}
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => setActiveModalType('projecten')}
                  className="text-base rounded-full py-3 px-5 border border-black cursor-pointer w-fit"
                >
                  + Toevoegen
                </button>
              </div>
            </div>
            <div className="col-span-2 col-start-2 flex flex-col gap-4 pb-8">
              <div className="flex flex-col">
                <label className="special-font pb-6">Gerelateerde personen</label>
                {selectedPersonen.length > 0 && (
                  <div className="flex flex-wrap gap-4 pb-4">
                    {selectedPersonen.map((item) => (
                      <div key={item.nid} className="flex items-center gap-2">
                        <div className="flex items-center gap-4">
                          <div className="max-h-16 aspect-square relative">
                            <div className="absolute bottom-1 -right-1 z-0">
                              {_getTileShape(item.image_shape, item.relaycolor, "64")}
                            </div>
                            <img
                              src={item.imageUrl}
                              alt={item.title}
                              className={`list-thumbnail w-full h-full object-cover relative z-10 clip-path-${item.image_shape}`}
                            />
                          </div>
                          <span className="!text-base">{item.title}</span>
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            setSelectedPersonen((prev) => prev.filter((p) => p.nid !== item.nid));
                            setAddedItems((prev) => prev.filter((nid) => nid !== item.nid));
                          }}
                          className="hover:opacity-80"
                        >
                          <img
                            src={_getBaseUrl("src/assets/inspiratiepunten/remove.svg", true)}
                            alt="Verwijder"
                            className="w-4 h-4 shrink-0"
                          />
                        </button>

                      </div>
                    ))}
                  </div>
                )}

                <button
                  type="button"
                  onClick={() => setActiveModalType('personen')}
                  className="text-base rounded-full py-3 px-5 border border-black cursor-pointer w-fit"
                >
                  + Toevoegen
                </button>
              </div>
            </div>
            <div className="col-span-2 col-start-2 flex flex-col gap-4 pb-8">
              <div className="flex flex-col">
                <label className="special-font pb-6">Gerelateerde materialen</label>
                {selectedMaterialen.length > 0 && (
                  <div className="flex flex-wrap gap-4 pb-4">
                    {selectedMaterialen.map((item) => (
                      <div key={item.nid} className="flex items-center gap-2">
                        <div className="flex items-center gap-4">
                          <div className="max-h-16 aspect-square relative">
                            <div className="absolute bottom-1 -right-1 z-0">
                              {_getTileShape(item.image_shape, item.relaycolor, "64")}
                            </div>
                            <img
                              src={item.imageUrl}
                              alt={item.title}
                              className={`list-thumbnail w-full h-full object-cover relative z-10 clip-path-${item.image_shape}`}
                            />
                          </div>
                          <span className="!text-base">{item.title}</span>
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            setSelectedMaterialen((prev) => prev.filter((p) => p.nid !== item.nid));
                            setAddedItems((prev) => prev.filter((nid) => nid !== item.nid));
                          }}
                          className="hover:opacity-80"
                        >
                          <img
                            src={_getBaseUrl("src/assets/inspiratiepunten/remove.svg", true)}
                            alt="Verwijder"
                            className="w-4 h-4"
                          />
                        </button>

                      </div>
                    ))}
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => setActiveModalType('materialen')}
                  className="text-base rounded-full py-3 px-5 border border-black cursor-pointer w-fit"
                >
                  + Toevoegen
                </button>
              </div>
            </div>
          </div>

          {activeModalType && (
            <div className="fixed inset-0 bg-black/30 z-50 flex items-center justify-center">
              <div className="bg-white rounded-md shadow-lg w-full max-w-lg h-screen py-12 px-8 space-y-4 relative">
                <h2 className="h3 font-sharpgrotesk text-black font-bold capitalize pb-4">Gerelateerde {activeModalType}</h2>

                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Typ om te zoeken..."
                  className="w-full border p-2 rounded"
                />

                <ul className="space-y-4 max-h-screen pt-4 mt-4">
                  {searchResults.map((item) => (
                    <li
                      key={item.nid}
                      className="flex justify-between items-center py-2 gap-4"
                    >
                      {/* Image and Title */}
                      <div className="flex items-center gap-4">
                        <div className="h-[60px] w-[60px] aspect-square relative">
                          <div className="absolute bottom-1 -right-1 z-0">
                            {_getTileShape(item.image_shape, item.relaycolor, "60")}
                          </div>
                          <img
                            src={item.imageUrl}
                            alt={item.title}
                            className={`list-thumbnail w-full h-full object-cover relative z-10 clip-path-${item.image_shape}`}
                          />
                        </div>
                        <div>
                          <div className="text-xl font-semibold font-sharpgrotesk text-cirBlack">{item.title}</div>
                          <p className="mb-0 text-base" style={{ color: item.relaycolor }}>{item.transitieagenda}</p>
                        </div>

                      </div>

                      {/* Add/Remove button */}
                      <button
                        type="button"
                        onClick={() => handleSelectInspiratiepunt(item)}
                        className="node-toevoegen xx w-10 h-10 shrink-0"
                      >
                        <img
                          src={
                            addedItems.includes(item.nid)
                              ? _getBaseUrl("src/assets/inspiratiepunten/tick-toevoegen.svg", true)
                              : _getBaseUrl("src/assets/inspiratiepunten/toevoegen.svg", true)
                          }
                          alt={addedItems.includes(item.nid) ? "Toegevoegd" : "Toevoegen"}
                          className="w-10 h-10"
                        />
                      </button>
                    </li>
                  ))}

                  {searchResults.length === 0 && (
                    <li className="text-gray-500 italic">Geen resultaten gevonden.</li>
                  )}
                </ul>

                <button
                  type="button"
                  onClick={() => {
                    setActiveModalType(null);
                    setSearchTerm('');
                    setSearchResults([]);
                  }}
                  className="hover:opacity-80 absolute top-4 right-6"
                >
                  <img
                    src={_getBaseUrl("src/assets/inspiratiepunten/close.svg", true)}
                    alt="Close Modal"
                    className="w-4 h-4"
                  />
                </button>
              </div>
            </div>
          )}


          {/* CATEGORY */}
          <h1 className="h3 font-sharpgrotesk text-black font-bold capitalize pb-4">Categorisering</h1>

          <div className="md:grid md:grid-cols-3 py-8">
            <label htmlFor="" className="pr-4">Tags</label>
            <div className="w-full relative col-span-1 col-start-2 flex flex-wrap gap-2">
              {selectedCategory.length === 0 && (
                <span className="text-gray-500 text-sm italic">Geen tags geselecteerd.</span>
              )}
              {selectedCategory.length > 0 && (
                <div className="flex flex-wrap gap-4 mb-4">
                  {categoryOptions
                    .filter((opt) => selectedCategory.includes(opt.value))
                    .map((opt) => (
                      <div
                        key={opt.value}
                        className="text-sm flex items-center gap-2"
                      >
                        <span>{opt.label}</span>
                        <button
                          type="button"
                          onClick={() =>
                            setSelectedCategory((prev) =>
                              prev.filter((id) => id !== opt.value)
                            )
                          }
                          className="hover:opacity-80"
                        >
                          <img
                            src={_getBaseUrl("src/assets/inspiratiepunten/remove.svg", true)}
                            alt="Verwijder"
                            className="w-4 h-4"
                          />
                        </button>
                      </div>
                    ))}
                </div>
              )}

            </div>
          </div>

          <div className="md:grid md:grid-cols-3 my-8">
            <label htmlFor="" className="pr-4">Tags toevoegen</label>
            <div className="w-full relative col-span-1 col-start-2 select-arrow">
              <MultiSelectDropdown
                options={categoryOptions}
                selected={selectedCategory}
                setSelected={setSelectedCategory}
                classes="text-base"
                searchable={true}
              />
            </div>
          </div>

          {/* { isAdmin === "1" && ( */}
          <div className="md:grid md:grid-cols-3 my-8">
            <div></div>
            <div className="w-full relative md:col-span-2 lg:col-span-1 col-start-2 flex gap-4">
              <div className="flex-grow">
                <TextInput
                    name="new-tag"
                    label="Nieuwe tag toevoegen"
                    placeholder=""
                    value={newTagValue}
                    onChange={(value) => setNewTagValue(value)}
                    onKeyUp={(e) => {
                      if (e.key === 'Enter' && newTagValue.trim() !== '') {
                        createNewInspiratieCategory(newTagValue);
                        setNewTagValue('');
                      }
                    }}
                    marginCss=""
                    error={newCategoryError !== null ? true : false}
                  />
                { newCategoryError && (
                  <div className="text-base text-red-500 mt-1">{newCategoryError}</div>
                ) }
              </div>
              <div className="flex-shrink-0 pt-7">
                <div style={{height: '50px'}}>
                  <button type="button" className="text-base rounded-full py-3 px-5 border border-black cursor-pointer w-fit flex-shrink-0"
                  onClick={() => {
                    if (newTagValue.trim() !== '') {
                      createNewInspiratieCategory(newTagValue);
                      setNewTagValue('');
                    }
                  }}
                  >+ Voeg toe</button>
                </div>
              </div>
            </div>
          </div>
          {/* )} */}

        </div>

        {(type === "projecten" || type === "") && (
          <div className="bg-[#F0EDE7]">
            <div className="container pt-12 pb-20">
              <h2 htmlFor="projectDocuments" className="h3 font-sharpgrotesk text-black font-bold capitalize pb-4">Project documenten</h2>
              <p className="italic">Let op: Deze documenten zijn beveiligd en voor intern gebruik. Deze worden niet op de het inspiratiecanvas getoond.</p>

              {/* Document Upload */}
              <ul className="text-sm text-gray-700 list-disc py-4">
                {documents.map((file, index) => (
                  <li key={index} className="flex gap-8 items-center pr-2 pb-2">
                    <div className="flex gap-x-2">
                      <img
                        src={_getBaseUrl("src/assets/inspiratiepunten/icon-document.svg", true)}
                        alt="Verwijder"
                        className="w-4 h-4"
                      />
                      <a href={file.url} target="_blank" rel="noopener noreferrer"><span className="underline">{file.name}</span></a>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleRemoveDocument(index)}
                      className="hover:opacity-80"
                    >
                      <img
                        src={_getBaseUrl("src/assets/inspiratiepunten/remove.svg", true)}
                        alt="Verwijder"
                        className="w-4 h-4"
                      />
                    </button>
                  </li>
                ))}
              </ul>

              <div className="flex items-center gap-4">
                <button
                  type="button"
                  onClick={() => document.getElementById("projectDocuments").click()}
                  className="text-base rounded-full py-3 px-5 border border-black cursor-pointer"
                >
                  + Document toevoegen
                </button>
                <input
                  type="file"
                  name="documents"
                  id="projectDocuments"
                  className="hidden"
                  onChange={handleDocumentChange}
                  multiple
                  accept=".pdf,.doc,.docx,.txt"
                />
              </div>

              {errors.documents && (
                <div className="text-red-600 col-span-3">{errors.documents}</div>
              )}

            </div>
          </div>
        )}

        <div className="container pt-8">
          {formMode === "edit" && (
            <div className="text-lg">
              Post Status: {postStatus ? postStatus.label : "Onbekend"}
            </div>
          )}

          {response && (
            <div className={`mt-4 text-lg ${response.status === "success" ? "text-green-600" : "text-red-600"}`}>
              {response.message}
            </div>
          )}

          {/* Submit */}
          <div className="flex flex-wrap gap-2 pt-4 items-center">
            {isAdmin ? (
              <>
                <a
                  href="/praktijkervaring/admin-dashboard"
                  onClick={() => setAction(null)}
                  disabled={loading}
                  className="whitespace-nowrap a-button flex items-center justify-center border-black hover:bg-black hover:text-white border px-6 py-2 rounded-full"
                >
                  {"Annuleren"}
                </a>

                <button
                  type="submit"
                  onClick={() => setAction("forpublish")}
                  disabled={loading}
                  className="whitespace-nowrap a-button flex items-center justify-center border-black hover:bg-black hover:text-white border px-6 py-2 rounded-full"
                >
                  {"Aanbieden ter publicatie"}
                </button>

                <button
                  type="submit"
                  onClick={() => setAction("concept")}
                  disabled={loading}
                  className="whitespace-nowrap a-button flex items-center justify-center border-black hover:bg-black hover:text-white border px-6 py-2 rounded-full"
                >
                  {"Afkeuren"}
                </button>

                <button
                  type="submit"
                  onClick={() => setAction("publish")}
                  disabled={loading}
                  className="whitespace-nowrap a-button flex justify-center items-center submit-btn border px-6 py-2 rounded-full"
                >
                  {"Publiceren"}
                </button>
              </>
            ) : (
              <>
                {(!postStatus || postStatus.value === "1" || postStatus.value === "3") && ( // Post current status is Concept or Published
                  <>
                    <a
                      href="/praktijkervaring/dashboard"
                      onClick={() => setAction(null)}
                      disabled={loading}
                      className="whitespace-nowrap a-button flex items-center justify-center border-black hover:bg-black hover:text-white border px-6 py-2 rounded-full"
                    >
                      Annuleren
                    </a>

                    <button
                      type="submit"
                      onClick={() => setAction("concept")}
                      disabled={loading}
                      className="whitespace-nowrap a-button flex items-center justify-center border-black hover:bg-black hover:text-white border px-6 py-2 rounded-full"
                    >
                      Opslaan als Concept
                    </button>

                    <button
                      type="submit"
                      onClick={() => setAction("forpublish")}
                      disabled={loading}
                      className="whitespace-nowrap a-button flex items-center justify-center submit-btn border px-6 py-2 rounded-full"
                    >
                      Aanbieden ter publicatie
                    </button>
                  </>
                )}

                {postStatus?.value === "2" && ( // Post current status is Awaiting for approval
                  <>
                    <a
                      href="/praktijkervaring/dashboard"
                      onClick={() => setAction(null)}
                      disabled={loading}
                      className="whitespace-nowrap a-button flex items-center justify-center border-black hover:bg-black hover:text-white border px-6 py-2 rounded-full"
                    >
                      Annuleren
                    </a>

                    <button
                      type="submit"
                      onClick={() => setAction("concept")}
                      disabled={loading}
                      className="whitespace-nowrap a-button flex items-center justify-center border-black hover:bg-black hover:text-white border px-6 py-2 rounded-full"
                    >
                      Opslaan als Concept
                    </button>

                    <div>
                      waiting for approval
                    </div>
                  </>

                )}
              </>
            )}

            {loading && <div className="loader"></div>}
          </div>
        </div>
      </form>

      <StateView
        title="wijk"
        values={ values.wijk }
        className="bottom-4 right-4 h-auto"
        floating
        
      />

      <StateView
        title="wijkOptions"
        values={ wijkOptions }
        position="top-4 right-4 h-auto"
        floating
        
      />

      <StateView
        title="selectedCategory"
        values={ selectedCategory }
        position="top-4 left-4 h-auto"
        floating
        
      />

      {/* <StateView
        title="posts"
        values={{ ...values, formMode, mediaId }}
        className="bottom-4 right-4 h-auto"
        floating
        collapsed
      /> */}

      {/* <StateView
        title="flexiBlocks"
        values={flexiBlocks}
        position="top-24 right-4 h-auto"
        floating
        collapsed
      /> */}
    </React.Fragment>
  );
};

export default ProjectCreateEdit;
